import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { formatLocalDateInTz } from '../../../utils/timezone';
import { cn } from '../../../utils/cn';
import type { DespatchChoice, DespatchResult, LoadShortfall } from '../../../types/nursery';

/**
 * Despatch a picked load. Stock leaves from the lots it was picked from, the
 * orders ship, and whatever each order was short is listed here with what
 * happens to it: backordered by default, into the order's own window. A
 * Nursery Admin can instead move a backorder to another season or cancel the
 * remainder; everyone else sees the default only, with the reason why.
 *
 * An order with nothing picked is backordered whole like any other
 * shortfall (Rob, 2026-10-15), and says so.
 *
 * The load need not be fully picked: when the truck has to leave, what was
 * not picked is short like anything else and is backordered.
 */

export interface DespatchDialogProps {
  loadId: number;
  loadNumber: string;
  timezone: string;
  isAdmin: boolean;
  /** Picking has not finished: the dialog says what is not picked stays behind. */
  unfinished?: boolean;
  onClose: () => void;
  /** tellCustomers: the person asked to email the load's customers next. */
  onDespatched: (result: DespatchResult, tellCustomers: boolean) => void;
}

type Mode = 'backorder' | 'later' | 'cancel';

export function DespatchDialog({ loadId, loadNumber, timezone, isAdmin, unfinished = false, onClose, onDespatched }: DespatchDialogProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'DespatchDialog' });
  const [shortfalls, setShortfalls] = useState<LoadShortfall[] | null>(null);
  const [modes, setModes] = useState<Record<number, Mode>>({});
  const [windows, setWindows] = useState<Record<number, { from: string; by: string }>>({});
  const [busy, setBusy] = useState(false);
  const [tell, setTell] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getLoadShortfalls(loadId)
      .then((res) => (res.success ? setShortfalls(res.data) : setError(errorMessage(res, t('readFailed')))))
      .catch((e) => setError(errorMessage(e, t('readFailed'))));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only words the fallback error
  }, [loadId]);

  const shorts = shortfalls ?? [];

  const despatch = async () => {
    const choices: DespatchChoice[] = shorts.flatMap((s): DespatchChoice[] => {
      const mode = modes[s.order_id] ?? 'backorder';
      if (mode === 'cancel') return [{ order_id: s.order_id, action: 'cancel' }];
      if (mode === 'later') {
        const w = windows[s.order_id];
        return [{ order_id: s.order_id, action: 'backorder', despatch_from: w?.from || null, despatch_by: w?.by || null }];
      }
      return [];
    });
    const missingWindow = shorts.some((s) => modes[s.order_id] === 'later' && !windows[s.order_id]?.from);
    if (missingWindow) {
      setError(t('missingWindow'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.despatchLoad(loadId, choices);
      if (res.success) onDespatched(res.data, tell);
      else setError(errorMessage(res, t('despatchFailed')));
    } catch (e) {
      setError(errorMessage(e, t('despatchFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="despatch-title">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="despatch-title" className="text-lg font-semibold text-neutral-900">{t('title', { load: loadNumber })}</h2>
            <p className="text-sm text-neutral-500">
              {t('intro')}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('close')} className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          {unfinished && (
            <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">{t('unfinished')}</p>
          )}
          {shortfalls === null ? (
            !error && <div className="h-20 bg-neutral-100 rounded-lg animate-pulse" />
          ) : shortfalls.length === 0 ? (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              {t('complete')}
            </p>
          ) : (
            <>
              {shorts.length > 0 && (
              <p className="text-sm text-neutral-700">
                {isAdmin ? t('shortAdmin', { count: shorts.length }) : t('shortStaff', { count: shorts.length })}
              </p>
              )}
              <ul className="space-y-3">
                {shorts.map((s) => {
                  const mode = modes[s.order_id] ?? 'backorder';
                  const short = s.lines.reduce((n, l) => n + l.short, 0);
                  return (
                    <li key={s.order_id} className="border border-neutral-200 rounded-lg p-3">
                      <p className="font-medium text-neutral-900 break-words">{s.order_number} · {s.customer_name}</p>
                      {s.nothing_picked && <p className="text-xs text-amber-800 mt-0.5">{t('nothingPicked')}</p>}
                      <ul className="mt-1 text-sm text-neutral-600 space-y-0.5">
                        {s.lines.map((l) => (
                          <li key={l.line_id} className="break-words">
                            {l.short} × {l.cultivar_name}{l.rootstock_name ? ` / ${l.rootstock_name}` : ''}, {l.tree_type_name}
                            <span className="text-neutral-400">{t('sent', { picked: l.picked, ordered: l.ordered })}</span>
                          </li>
                        ))}
                      </ul>
                      {s.paid_online && (
                        <p className="text-xs text-neutral-500 mt-1">
                          {t('paidOnline')}
                        </p>
                      )}
                      {isAdmin && (
                        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={t('choiceLabel', { order: s.order_number })}>
                          {([
                            ['backorder', t('modeBackorder', { short })],
                            ['later', t('modeLater')],
                            ['cancel', t('modeCancel')],
                          ] as [Mode, string][]).map(([m, label]) => (
                            <button key={m} type="button" aria-pressed={mode === m}
                              onClick={() => setModes((x) => ({ ...x, [s.order_id]: m }))}
                              className={cn('rounded-lg border px-3 py-1.5 text-sm',
                                mode === m ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium' : 'bg-white border-neutral-300 text-neutral-600')}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                      {mode === 'backorder' && (
                        <p className="text-xs text-neutral-500 mt-2">
                          {s.despatch_by
                            ? t('keepsWindowBy', { date: formatLocalDateInTz(s.despatch_by, timezone) })
                            : t('keepsWindow')}
                        </p>
                      )}
                      {mode === 'later' && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label htmlFor={`bo-from-${s.order_id}`} className="block text-xs text-neutral-500 mb-1">{t('notBefore')}</label>
                            <input id={`bo-from-${s.order_id}`} type="date" value={windows[s.order_id]?.from ?? ''}
                              onChange={(e) => setWindows((w) => ({ ...w, [s.order_id]: { from: e.target.value, by: w[s.order_id]?.by ?? '' } }))}
                              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                          </div>
                          <div>
                            <label htmlFor={`bo-by-${s.order_id}`} className="block text-xs text-neutral-500 mb-1">{t('mustGoBy')}</label>
                            <input id={`bo-by-${s.order_id}`} type="date" value={windows[s.order_id]?.by ?? ''}
                              min={windows[s.order_id]?.from || undefined}
                              onChange={(e) => setWindows((w) => ({ ...w, [s.order_id]: { from: w[s.order_id]?.from ?? '', by: e.target.value } }))}
                              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                          </div>
                        </div>
                      )}
                      {mode === 'cancel' && (
                        <p className="text-xs text-amber-800 mt-2">{t('cancelNote', { short })}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <label className="flex items-start gap-2 px-5 pb-3 text-sm text-neutral-700">
          <input type="checkbox" className="mt-0.5" checked={tell} onChange={(e) => setTell(e.target.checked)} />
          <span>{t('tellCustomers')}</span>
        </label>
        <div className="flex flex-wrap justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>{t('notYet')}</Button>
          <Button size="sm" onClick={despatch} disabled={busy || shortfalls === null}>{busy ? t('despatching') : t('despatchNow')}</Button>
        </div>
      </div>
    </div>
  );
}

export default DespatchDialog;
